'use client';

import { useEffect } from 'react';

/** Standard Meta events that use fbq('track', ...) instead of fbq('trackCustom', ...) */
const META_STANDARD_EVENTS = new Set([
  'ViewContent', 'Lead', 'SubmitApplication', 'Schedule', 'PageView',
  'Purchase', 'AddToCart', 'CompleteRegistration', 'Contact', 'Search',
  'InitiateCheckout', 'AddPaymentInfo', 'AddToWishlist', 'FindLocation',
  'StartTrial', 'Subscribe', 'CustomizeProduct', 'Donate',
]);

interface MetaPixelScriptProps {
  pixelIds: string[];
  /** Resolved event name for PageView (from team trigger config) */
  pageViewEventName?: string;
}

function getCookie(name: string): string | null {
  // Avoid building a RegExp from a variable name (ReDoS risk). Parse the
  // cookie header directly by splitting on "; " and matching the prefix.
  const prefix = name + '=';
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const cookie of cookies) {
    if (cookie.startsWith(prefix)) {
      return decodeURIComponent(cookie.slice(prefix.length));
    }
  }
  return null;
}

/**
 * Injects the Meta Pixel base code and manages fbp/fbc via sessionStorage.
 * Fires the configurable PageView event on mount.
 */
export function MetaPixelScript({ pixelIds, pageViewEventName = 'ViewContent' }: MetaPixelScriptProps) {
  useEffect(() => {
    if (pixelIds.length === 0) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    // Record which pixels are initialized so fireMetaPixelBrowserEvent can
    // refresh their advanced-matching data. Set before the loaded guard so
    // it's always current even when the base code already ran.
    w.__scMetaPixelIds = pixelIds;
    if (w.fbq?.loaded) return;

    // Inject Meta Pixel base code
    const n = (w.fbq = function (...args: unknown[]) {
      if (n.callMethod) {
        n.callMethod(...args);
      } else {
        n.queue.push(args);
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }) as any;
    if (!w._fbq) w._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [] as unknown[];

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);

    // Init each pixel
    for (const pixelId of pixelIds) {
      w.fbq('init', pixelId);
    }

    // Fire PageView with configured event name
    if (META_STANDARD_EVENTS.has(pageViewEventName)) {
      w.fbq('track', pageViewEventName);
    } else {
      w.fbq('trackCustom', pageViewEventName);
    }

    // Store fbp and fbc from cookies
    const fbp = getCookie('_fbp');
    const fbc = getCookie('_fbc');
    if (fbp) sessionStorage.setItem('meta_fbp', fbp);
    if (fbc) sessionStorage.setItem('meta_fbc', fbc);

    // Build fbc from fbclid query param if present
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get('fbclid');
    if (fbclid) {
      const fbcValue = `fb.1.${Date.now()}.${fbclid}`;
      sessionStorage.setItem('meta_fbc', fbcValue);
    }
  }, [pixelIds, pageViewEventName]);

  return null;
}

/** Customer data for Meta advanced matching — same shape the CAPI call sends. */
export interface MetaPixelUserData {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  city?: string;
  state?: string;
  country?: string;
}

/**
 * Normalize per Meta's advanced-matching spec. fbevents.js SHA-256 hashes
 * every one of these fields in the browser before transmission — raw values
 * never leave the page.
 */
function toAdvancedMatching(u: MetaPixelUserData): Record<string, string> {
  const am: Record<string, string> = {};
  if (u.email) am.em = u.email.toLowerCase().trim();
  if (u.phone) am.ph = u.phone.replace(/\D/g, '');
  if (u.firstName) am.fn = u.firstName.toLowerCase().trim();
  if (u.lastName) am.ln = u.lastName.toLowerCase().trim();
  if (u.city) am.ct = u.city.toLowerCase().replace(/\s/g, '');
  if (u.state) am.st = u.state.toLowerCase().replace(/\s/g, '');
  if (u.country) am.country = u.country.toLowerCase().trim();
  return am;
}

/**
 * Fire a Meta Pixel browser event. Automatically chooses track vs trackCustom
 * based on whether the event name is a standard Meta event.
 * Returns the event_id for CAPI deduplication.
 *
 * Pass `userData` so the browser copy of the event carries the same match keys
 * as the CAPI copy: Meta dedups by keeping the FIRST copy it receives (usually
 * the browser one) and discards the other — identical keys on both sides make
 * that race lossless. Re-initing an already-initialized pixel updates its
 * advanced-matching data.
 *
 * NOTE (Jul 2026): the booking-widget funnel call sites (potential/qualified/
 * booked) are commented out by product decision — CAPI is the single source
 * for funnel events; only the page-view fire in MetaPixelScript remains
 * browser-side. This helper stays ready for re-enabling.
 */
export function fireMetaPixelBrowserEvent(eventName: string, userData?: MetaPixelUserData): string {
  const eventId = crypto.randomUUID();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  const fbq = w.fbq;
  if (fbq) {
    const am = userData ? toAdvancedMatching(userData) : {};
    if (Object.keys(am).length > 0) {
      const pixelIds: string[] = Array.isArray(w.__scMetaPixelIds) ? w.__scMetaPixelIds : [];
      for (const pixelId of pixelIds) {
        fbq('init', pixelId, am);
      }
    }
    if (META_STANDARD_EVENTS.has(eventName)) {
      fbq('track', eventName, {}, { eventID: eventId });
    } else {
      fbq('trackCustom', eventName, {}, { eventID: eventId });
    }
  }

  return eventId;
}
